import mermaid from 'mermaid';
import { useEffect, useRef, useState } from 'react';
import { ImageOverlay } from '../../Shared/ImageOverlay';

export const MermaidRenderer: React.FC<{
    code: string;
    theme?: 'default' | 'neutral' | 'dark' | 'forest' | 'base';
    themeVariables?: Record<string, string | number>;
}> = ({ code, theme = 'default', themeVariables }) => {
    const chartIdRef = useRef<string>(`mermaid-${Math.random().toString(36).slice(2)}`);
    const chartId = chartIdRef.current;
    const [svgContent, setSvgContent] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [showError, setShowError] = useState(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;
        setShowError(false);
        if (!code || !code.trim()) {
            setSvgContent('');
            setError(null);
            setLoading(false);
            return;
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        setLoading(true);
        setError(null);
        debounceTimeout.current = setTimeout(() => {
            mermaid.initialize({
                startOnLoad: false,
                suppressErrorRendering: true,
                securityLevel: 'loose',
                theme,
                ...(theme === 'base' && themeVariables ? { themeVariables } : {}),
            });
            const renderChart = async () => {
                try {
                    const { svg } = await mermaid.render(chartId, code);
                    if (!cancelledRef.current) {
                        setSvgContent(svg);
                        setError(null);
                    }
                } catch (err) {
                    if (!cancelledRef.current) {
                        setSvgContent('');
                        setError(
                            'Unfortunately, the diagram produced contained an error and it cannot be displayed. Try asking again.',
                        );
                        setShowError(false);
                        console.log('[MermaidRenderer] Rendering error at', new Date().toISOString(), err);
                    }
                } finally {
                    if (!cancelledRef.current) setLoading(false);
                }
            };
            renderChart();
        }, 300); // 300ms debounce
        return () => {
            cancelledRef.current = true;
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [chartId, code, theme, themeVariables]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setShowError(true), 1000);
            return () => clearTimeout(timer);
        } else {
            setShowError(false);
        }
    }, [error]);

    const isSvgReady =
        svgContent && !/^\s*<div.*?class=["']mermaid["'].*?>\s*HTTP.*?error.*?<\/div>\s*$/i.test(svgContent.trim());

    if (loading) {
        return (
            <div className="mermaid-container my-4 w-full overflow-hidden">
                <div className="text-gray-400 animate-pulse mb-2">Rendering diagram...</div>
                <pre className="bg-gray-100 text-xs p-2 rounded overflow-x-auto border border-gray-200 dark:text-gray-800 max-w-full">
                    <code>{code}</code>
                </pre>
            </div>
        );
    }
    if (error) {
        return (
            <div
                className={`text-red-500 p-2 border border-red-300 bg-red-50 rounded transition-opacity duration-500 ${showError ? 'opacity-100' : 'opacity-0'}`}>
                {error}
            </div>
        );
    }
    return (
        <div className="mermaid-container my-4 w-full overflow-hidden">
            {isSvgReady ? (
                <div className="relative group w-full">
                    <div
                        className="cursor-pointer hover:opacity-90 transition-opacity w-full overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: svgContent }}
                        onClick={() => setIsOverlayOpen(true)}
                    />

                    {/* Hover overlay to indicate clickability */}
                    <div
                        className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-start justify-end p-2 cursor-pointer"
                        onClick={() => setIsOverlayOpen(true)}
                    >
                        <div className="bg-white/90 dark:bg-gray-800/90 px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                            Click to enlarge
                        </div>
                    </div>

                    <ImageOverlay
                        open={isOverlayOpen}
                        onClose={() => setIsOverlayOpen(false)}
                        svgContent={svgContent}
                        altText="Mermaid diagram"
                    />
                </div>
            ) : (
                <div className="text-gray-400 animate-pulse">Rendering diagram...</div>
            )}
        </div>
    );
};
